/**
 * InputSourceToggle V2
 *
 * Toggle-Switch zwischen zwei Eingabequellen:
 * - "upload": Neues Bild hochladen (Drag & Drop)
 * - "workspace": Aus Workspace-Gallery auswählen
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FolderOpen } from 'lucide-react';
import { cn } from '@/lib/utils';

export type InputSource = 'upload' | 'workspace';

export interface InputSourceToggleProps {
  value: InputSource;
  onChange: (source: InputSource) => void;
  disabled?: boolean;
  className?: string;
}

export function InputSourceToggle({
  value,
  onChange,
  disabled = false,
  className,
}: InputSourceToggleProps) {
  const { t } = useTranslation();
  const handleChange = useCallback(
    (source: InputSource) => {
      if (!disabled && source !== value) {
        onChange(source);
      }
    },
    [value, onChange, disabled]
  );

  return (
    <div className={cn('space-y-2', className)}>
      {/* Toggle Buttons */}
      <div
        className="inline-flex rounded-lg border border-neutral-200 dark:border-neutral-700 p-1 bg-neutral-100 dark:bg-neutral-800"
        role="group"
        aria-label={t('workspace.inputSourceAria')}
      >
        {/* Upload Button */}
        <button
          type="button"
          onClick={() => handleChange('upload')}
          disabled={disabled}
          aria-pressed={value === 'upload'}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            value === 'upload'
              ? 'bg-white dark:bg-neutral-900 text-primary-600 dark:text-primary-400 shadow-sm'
              : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <Upload className="h-4 w-4" />
          <span>{t('generation.uploadNew')}</span>
        </button>

        {/* Workspace Button */}
        <button
          type="button"
          onClick={() => handleChange('workspace')}
          disabled={disabled}
          aria-pressed={value === 'workspace'}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            value === 'workspace'
              ? 'bg-white dark:bg-neutral-900 text-primary-600 dark:text-primary-400 shadow-sm'
              : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <FolderOpen className="h-4 w-4" />
          <span>{t('generation.gallery')}</span>
        </button>
      </div>

      {/* Helper Text */}
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        {value === 'upload'
          ? t('generation.uploadNewDesc')
          : t('generation.selectImageFromGallery')}
      </p>
    </div>
  );
}

export default InputSourceToggle;
